import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, type Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { FileUtilService, SelectedFile, UploadFileStatusEnum } from './file-util.service';

@Injectable({
  providedIn: 'root',
})
export class SelectRawFilesUtil {
  private fileUtilService = inject(FileUtilService);

  private translateService = inject(TranslateService);

  selectRawFiles(
    selectedFileBlobs: File[],
    allowMultiple: boolean,
    maxFileSize: number,
    requiredContentType: string,
    targetFile?: SelectedFile,
  ): Observable<SelectedFile[]> {
    const observables: Observable<SelectedFile>[] = [];

    if (allowMultiple) {
      // process all files
      // since we expect multiple files, we only want to apply the first file to targetFile
      let target = targetFile;
      for (const selectedFileBlob of selectedFileBlobs) {
        // push observable
        observables.push(this.selectRawFile(selectedFileBlob, maxFileSize, requiredContentType, target));

        // unset target, only want to use it for first file
        target = undefined;
      }

      return forkJoin(observables);
    } else {
      // process first file
      const selectFileObservable = this.selectRawFile(
        selectedFileBlobs[0],
        maxFileSize,
        requiredContentType,
        targetFile,
      );

      return combineLatest([selectFileObservable]);
    }
  }

  private selectRawFile(
    selectedFileBlob: File,
    maxFileSize: number,
    requiredContentType: string,
    targetFile?: SelectedFile,
  ): Observable<SelectedFile> {
    if (!selectedFileBlob) {
      // @ts-expect-error TS2322 type not assignable
      return of(null);
    }

    let selectedFile: SelectedFile;

    if (targetFile) {
      // use specified selectedFile
      selectedFile = targetFile;
      selectedFile.reset();
    } else {
      // set selectedFileData
      selectedFile = new SelectedFile();
    }

    // set data
    selectedFile.setData({
      fileBlob: selectedFileBlob,
      fileData: {
        fileName: selectedFileBlob.name,
        fileSize: selectedFileBlob.size,
        contentType: selectedFileBlob.type,
      },
    });

    // check filesize
    if (maxFileSize && selectedFileBlob.size > maxFileSize) {
      // set error status
      selectedFile.setError(
        this.translateService.instant('qz-X-too-big-maximum-size-X-zm', {
          name: selectedFile.fileData.fileName,
          size: this.fileUtilService.getWordedSize(maxFileSize),
        }),
      );

      // don't continue
      return throwError(selectedFile);
    }

    //check contentType; special check for image
    if (
      requiredContentType &&
      // @ts-expect-error TS2345 argument not assignable
      requiredContentType !== this.fileUtilService.getFileTypeByContentType(selectedFile.fileData.contentType)
    ) {
      // set error status
      const errorMessage = this.translateService.instant('qz-incorrect-file-format-X-file-zm', {
        requiredContentType,
      });

      if (requiredContentType === 'image') {
        selectedFile.errorMessage = this.translateService.instant('qz-incorrect-file-format-please-select-image-zm');
      }

      selectedFile.setError(errorMessage);

      // don't continue
      return throwError(selectedFile);
    }

    // generate preview async
    return this.processFile(selectedFile, selectedFileBlob);
  }

  private processFile(selectedFileData: SelectedFile, selectedFileBlob: File): Observable<SelectedFile> {
    // set status to processing
    selectedFileData.setStatus(UploadFileStatusEnum.processing);

    return this.fileUtilService.getFileImagePreview(selectedFileBlob).pipe(
      map((base64Image) => {
        // set the preview dataUrl on the thumbnail dataobject
        selectedFileData.fileData.previewDataURL = base64Image;

        return selectedFileData;
      }),
    );
  }
}
